// The accountability log as it's drawn (docs/mastermind-admin-plan.md 4.4 and 4.5): from the document
// the database writes (private.accountability_log, read as hub.my_accountability_log by the member and
// hub.accountability_log by owner and admin) to every line Log.jsx draws. Pure: dates go through
// src/lib/format.js in UK time, and nothing here reads the clock, so the same document always draws the
// same log, for the member, for Adam and in a copy kept when it was sent or saved.
//
// What the log counts is worked out here, from the months' actions and outcomes and every session's result
// (accountability, below), never from the database's guarantee state (Adam, 18 Oct, 15:30: "You're still confusing the
// guarantee with accountability"). It reads only what the member's version holds, so the staff version draws the same
// page: no register note, staff name, outcome's author or "Worked out" tag is ever drawn here. Since migration 81 the
// member's version holds none of the guarantee (decision 56: rules, and summary's state, off, edges, limit, needed, pct,
// each measure's state, the goal's days, due_on, on and judged), and nothing here reads them, so a document from before
// 81 (a staff version, or live before 81 runs) draws the same page.
//
// The member's page and Adam's tab draw the same log: what's done and what isn't, and the attendance (attended, missed
// and caught up on the recording, missed and the recording not watched), as plain facts. Never a limit, a percentage,
// a threshold or How on track is worked out: the guarantee and its rules stay in Masterminds, apart from the log.
import { uk, calendarDay } from '../../lib/format.js'
import { LOG, LOG_MEMBER } from './logWords.js'
import { isBehind, levelFromLog, withSet } from './levels.js'

const DAYS3 = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS3 = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// A moment or a calendar day ('2026-08-02') in UK time
const at = v => (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? calendarDay(v) : uk(v))
// 3 Oct
export const dm = v => { const u = at(v); return `${u.d} ${MONTHS3[u.m - 1]}` }
// 2 Aug 2026
export const dmy = v => { const u = at(v); return `${u.d} ${MONTHS3[u.m - 1]} ${u.y}` }
// Sun 27 Sep
export const wdm = v => { const u = at(v); return `${DAYS3[u.dow]} ${u.d} ${MONTHS3[u.m - 1]}` }
// Tue 13 Oct 2026
export const wdmy = v => { const u = at(v); return `${DAYS3[u.dow]} ${u.d} ${MONTHS3[u.m - 1]} ${u.y}` }
// 9:40pm
export const hm = v => { const { hh, mm } = at(v); return (hh % 12 || 12) + (mm ? ':' + String(mm).padStart(2, '0') : '') + (hh < 12 ? 'am' : 'pm') }

// "3 to 12 Nov 2026", "28 Oct to 3 Nov 2026", or "since 3 Nov 2026" while it lasts
export function spanWords({ from, to }) {
  const a = at(from)
  if (!to) return LOG.span(dmy(from), null)
  const b = at(to)
  if (a.y === b.y && a.m === b.m) return LOG.span(String(a.d), dmy(to))
  if (a.y === b.y) return LOG.span(dm(from), dmy(to))
  return LOG.span(dmy(from), dmy(to))
}
const spansWords = list => (list || []).map(spanWords).join(' and ')

// What the log counts (Adam, 18 Oct): every reviewed month's actions, each month's own, so an action not done on
// time is not done in the month it was set (carried over or not), and every session, attended, caught up on the
// recording (on time or late) or missed with the recording not watched. Excused and paused sessions, sessions with no
// register, and actions set aside by agreement aren't counted. The state is its level (levels.js, Adam, 18 Oct,
// 15:45): On track only with every session attended live and every action done, then A little behind, Behind and
// Well behind by how much has slipped, and Getting started before any session is held or any action falls due. A
// level Adam set by hand (the document's level, only while 1 is set: migration 80) goes over the automatic level, which
// stays as auto, with the counts as they are: the record is the record (levels.js withSet).
export function accountability(doc) {
  const months = doc?.months || []
  const a = { due: 0, done: 0, late: 0, notDone: 0, carried: 0, part: 0, setAside: 0, carriedOpen: 0 }
  for (const m of months) {
    for (const x of m.actions || []) {
      if (x.result === 'set_aside') { if (m.closed_at) a.setAside++; continue }
      // carried into the open month and not ticked yet: due, and not done until it's ticked (Adam, 25 Oct)
      if (!m.closed_at) { if (x.hops > 0 && !(x.ticked || x.done_at)) { a.carriedOpen++; a.due++; a.notDone++; a.carried++ } continue }
      // Adam, 25 Oct: an action carried over is counted once, where it ends. Done in a later month is done late, and
      // no longer a slip; never done stays not done to the end of the term; carried and still open is a slip until
      // it's done (carriedOpen, above). So the month it was carried from doesn't count it as due or not done.
      if (x.result !== 'done' && x.carried_to) continue
      a.due++
      if (x.result === 'done') { a.done++; if (x.hops > 0) a.late++ } else {
        a.notDone++
        if (x.result === 'part_done') a.part++
      }
    }
  }
  const s = { counted: 0, live: 0, caught: 0, late: 0, missed: 0, excused: 0, paused: 0, unrecorded: 0 }
  for (const x of [...(doc?.before_months || []), ...months.flatMap(m => m.sessions || [])]) {
    if (x.result === 'attended') s.live++
    else if (x.result === 'caught_up') s.caught++
    else if (x.result === 'missed') { if (x.caught_up_on) s.late++; else s.missed++ }
    else { if (x.result === 'excused') s.excused++; else if (x.result === 'paused') s.paused++; else s.unrecorded++; continue }
    s.counted++
  }
  s.present = s.live + s.caught + s.late
  const out = { actions: a, sessions: s }
  out.auto = levelFromLog(out)
  out.level = withSet(out.auto, doc?.level)
  out.state = out.level.key
  return out
}

// 2. Where you stand: the level, what's slipped said plainly, and a short push in Adam's voice, picked by what slipped
// (logWords.js causeOf: the Accelerator critique's member 16, 20 Oct). A level set by hand whose slips sit outside its
// own band (levels.js withSet's fits) uses the words that name no count: On track without its facts line, and A little
// behind's own push (LOG.pushSet). What's slipped is said as it is, and not at all when nothing has.
export function standLines(level, acc) {
  const plain = Boolean(level.set && !level.fits)
  const lines = []
  if (level.key === 'getting_started') lines.push(LOG.gettingStarted)
  else if (level.key === 'on_track') lines.push(...(plain ? [LOG.push.on_track()] : [LOG.onTrack({ due: level.due, held: level.held }), LOG.push.on_track()]))
  else {
    lines.push(LOG.slipped(level) || null, plain && LOG.pushSet[level.key] ? LOG.pushSet[level.key] : LOG.push[level.key](level))
    if (acc?.actions?.carried || acc?.actions?.carriedOpen) lines.push(LOG.carriedMeans)
    if (level.caught && !level.never) lines.push(LOG.caughtMeans)
  }
  return lines.filter(Boolean)
}
// said: how many of the first lines the refocus card says too (what's slipped and the push), so while the card leads
// the member's page the verdict is said once on screen (member 3, 20 Oct) and kept in print
export function standing(doc) {
  const acc = accountability(doc)
  const { state } = acc
  const lines = standLines(acc.level, acc)
  if (!goalAgreed(doc.summary?.goal)) lines.push(LOG.goalToDo)
  return { state, behind: isBehind(state), headline: LOG.state[state], lines, said: isBehind(state) ? (LOG.slipped(acc.level) ? 2 : 1) : 0, acc }
}
const goalAgreed = go => ['agreed', 'agreed_late', 'agreed_unjudged', 'reached'].includes(go?.state)

// 3. The 4 measures, each { key, h, figure, label, detail, extra, mark, bar }. Only actions and sessions carry Slipped,
// and the membership Overdue while a payment is. pay: the member's own page's { by: '2 October', other } from their
// access (member 13, 20 Oct): the day everything stays open until, and whether someone else pays for the place; Adam's
// tab and a kept copy have none, so their overdue line has no day.
export function measures(doc, { pay = null } = {}) {
  const sum = doc.summary || {}
  const { actions: ac, sessions: se } = accountability(doc)
  const go = sum.goal || {}
  const me = sum.membership || {}
  const out = []

  const sessExtra = [se.excused ? LOG.sessions.excused(se.excused) : null, se.paused ? LOG.sessions.paused(se.paused) : null,
    se.unrecorded ? LOG.sessions.unrecorded(se.unrecorded) : null].filter(Boolean)
  out.push(se.counted
    ? { key: 'sessions', h: LOG.sessions.h, figure: `${se.live} of ${se.counted}`, label: LOG.sessions.label,
        detail: LOG_MEMBER.sessionsDetail({ live: se.live, caught: se.caught, missed: se.missed + se.late, late: se.late }),
        extra: sessExtra, mark: se.live < se.counted ? 'off' : 'on' }
    : { key: 'sessions', h: LOG.sessions.h, figure: LOG.sessions.none, label: null, detail: LOG.sessions.noneLine, extra: sessExtra, mark: null })

  // carried into the open month and not ticked: said beside the figure, and counted as not done until it's ticked
  const carried = ac.carriedOpen ? LOG.actions.carried(ac.carriedOpen) : null
  // the open month's actions wait for Adam's review once the session that reviews them has been held
  const open = (doc.months || []).find(m => !m.closed_at && !m.after_term)
  const dueNext = open ? (open.actions || []).filter(x => x.result !== 'set_aside').length : 0
  const due = n => (open && reviewSessionOf(open) ? LOG.actions.dueReview(n) : LOG.actions.due(n))
  const acExtra = [dueNext ? due(dueNext) : null, ac.setAside ? LOG.actions.setAside(ac.setAside) : null].filter(Boolean)
  const pct = n => (ac.due ? Math.round((n || 0) * 1000 / ac.due) / 10 : 0)
  out.push(ac.due
    ? { key: 'actions', h: LOG.actions.h, figure: `${ac.done} of ${ac.due}`, label: LOG.actions.label,
        detail: LOG.actions.detail({ done: ac.done, due: ac.due, notDone: ac.notDone, carried: ac.carried, part: ac.part }),
        carried, extra: acExtra, mark: ac.notDone ? 'off' : 'on',
        bar: [['on_time', pct(ac.done - ac.late)], ['late', pct(ac.late)], ['part', pct(ac.part)], ['not', pct(ac.notDone - ac.part)]] }
    : { key: 'actions', h: LOG.actions.h, figure: LOG.actions.none, label: null, detail: LOG.actions.noneLine, carried, extra: acExtra, mark: null })

  // the goal as a fact: agreed (and the day), reached, or still to agree; never the days a rule allows
  const goal = go.state === 'reached'
    ? { figure: LOG.goal.reached, detail: LOG.goal.reachedLine(dmy(go.reached_on)), mark: 'on' }
    : goalAgreed(go) && go.agreed_on
      ? { figure: LOG.goal.agreed, detail: LOG.goal.agreedLine(dmy(go.agreed_on)), mark: 'on' }
      : { figure: LOG.goal.notYet, detail: LOG.goal.toDoLine, mark: 'todo' }
  out.push({ key: 'goal', h: LOG.goal.h, label: null, extra: [], ...goal })

  const suspended = me.suspended || []
  // a payment overdue now: "Payment overdue. Update your card by 2 October to keep everything open.", never Up to date
  // beside it (member 13)
  const overdueLine = pay?.other ? LOG.membership.overdueOther(pay?.by || null) : LOG.membership.overdueLine(pay?.by || null)
  const pausedLine = (me.paused || []).length ? LOG.membership.paused(spansWords(me.paused)) : null
  if (suspended.length) {
    const extra = [pausedLine, me.overdue ? `${LOG.membership.overdueH}. ${overdueLine}` : null].filter(Boolean)
    out.push({ key: 'membership', h: LOG.membership.h, figure: LOG.membership.suspended, label: null, detail: LOG.membership.suspendedLine(spansWords(suspended)), extra, mark: null })
  } else if (me.overdue) {
    out.push({ key: 'membership', h: LOG.membership.h, figure: LOG.membership.overdueH, label: null, detail: overdueLine, extra: [pausedLine].filter(Boolean), mark: 'overdue' })
  } else {
    out.push({ key: 'membership', h: LOG.membership.h, figure: LOG.membership.upToDate, label: null, detail: LOG.membership.upToDateLine, extra: [pausedLine].filter(Boolean), mark: 'on' })
  }
  return out
}

// A month's own mark: a reviewed month is Behind with any action not done or any session not attended live (caught up
// on the recording or not), and On track once everything in it is done; paused, no actions and in progress as the database has them
function counted(m) {
  const acts = (m.actions || []).filter(x => x.result !== 'set_aside')
  const ses = m.sessions || []
  const missed = ses.filter(x => x.result === 'missed' && !x.caught_up_on).length
  const caught = ses.filter(x => x.result === 'caught_up' || (x.result === 'missed' && x.caught_up_on)).length
  return { due: acts.length, done: acts.filter(x => x.result === 'done').length, missed: missed + caught, caught }
}
export function monthMark(m) {
  if (!m.closed_at) return m.mark || 'in_progress'
  const c = counted(m)
  if (c.done < c.due || c.missed) return 'off_track'
  if (c.due) return 'on_track'
  return m.mark === 'paused' || m.mark === 'no_actions' ? m.mark : 'on_track'
}

// 4. The strip of months 1 to 12: { n, mark, word, aria, id }. A month up to the one they're in now (the header's) with
// no actions is No actions set, so the strip and the header never disagree; later months are To come. id: the month's
// own (its cycle), which a box on the member's page jumps to (member 8, 20 Oct), or null.
export function strip(doc) {
  const months = doc.product?.term_months || doc.summary?.term_months || 12
  const now = doc.summary?.term_month || doc.access?.term_month || 1
  const by = new Map((doc.months || []).filter(m => !m.after_term).map(m => [m.month, m]))
  return Array.from({ length: months }, (_, i) => {
    const n = i + 1
    const m = by.get(n)
    const mark = m ? monthMark(m) : n <= now ? 'no_actions' : 'to_come'
    const word = LOG.stripMark[mark] || LOG.stripMark.to_come
    return { n, mark, word, aria: LOG.stripAria(n, word), id: m?.cycle_id || null }
  })
}

// How a session went, as a month's line says it, with whether the recording was watched
function sessionHow(x) {
  const H = { ...LOG.how, ...LOG_MEMBER.how }
  switch (x.result) {
    case 'attended': return H.attended
    case 'caught_up': return H.caught_up(dm(x.caught_up_on))
    case 'missed': return x.caught_up_on ? H.missedLate(dm(x.caught_up_on)) : H.missed
    case 'excused': return LOG.how.excused
    case 'paused': return LOG.how.paused
    default: return LOG.how.unrecorded
  }
}

// 1 action in its month: { id, body, mark, word, detail, extra }. In the open month a ticked action is Ticked (the
// member's own tick, before Adam's review), an action carried in and not ticked is Still not done, and the rest are
// Due; once the session that reviews the month has been held (reviewHeld), they wait for Adam's review rather than
// the next session.
export function actionView(a, m, reviewDay, reviewHeld = false) {
  const open = !m.closed_at
  let mark
  let detail
  if (a.result === 'set_aside') {
    mark = 'set_aside'
    detail = LOG.setAside(a.set_aside_on ? dm(a.set_aside_on) : '', a.set_aside_reason)
  } else if (open) {
    const carried = a.hops > 0 ? LOG.carriedOpen(a.first_month, a.hops) : null
    const ticked = a.ticked || Boolean(a.done_at)
    if (ticked) {
      mark = 'ticked'
      detail = [carried, LOG.youTicked(a.done_at ? dm(a.done_at) : null), reviewHeld ? LOG.reviewToCome : LOG.atNextSession].filter(Boolean).join(' ')
    } else if (carried) {
      mark = 'carried'
      detail = reviewHeld ? `${carried} ${LOG.notTickedReview}` : `${carried} ${LOG.stillNotDone}`
    } else {
      mark = 'due'
      detail = reviewHeld ? LOG.notTickedReview : LOG.due
    }
  } else if (a.result === 'done') {
    if (a.hops > 0) { mark = 'done_late'; detail = LOG.doneLate(a.first_month, m.month, a.hops) } else {
      mark = 'done'
      detail = a.done_at ? LOG.ticked(dm(a.done_at)) : m.copied ? LOG.doneCopied : reviewDay ? LOG.doneAtReview(reviewDay) : LOG.doneAtReviewNoDay
    }
  } else if (a.carried_to) {
    mark = a.result === 'part_done' ? 'part_done' : 'not_done'
    detail = LOG.carriedTo(a.carried_to, a.result === 'part_done')
  } else if (a.result === 'part_done') {
    mark = 'part_done'
    detail = LOG.partDone(reviewDay)
  } else {
    mark = 'not_done'
    detail = a.hops > 0 ? LOG.notDoneAfter(a.hops, reviewDay) : LOG.notDone(reviewDay)
  }
  const extra = []
  if (a.note) extra.push(LOG.note(a.note))
  if (a.unticked?.ticked_at && a.unticked?.unticked_at) extra.push(LOG.unticked(dm(a.unticked.ticked_at), dm(a.unticked.unticked_at)))
  if (a.corrected_on) extra.push(LOG.updated(dm(a.corrected_on)))
  return { id: a.action_id, body: a.body, mark, word: LOG.actionMark[mark], detail, extra }
}

// The open month's review: a session held since the month was set reviews it, and Adam records that review. The
// last such session's start, or null while none has been held.
function reviewSessionOf(m) {
  const held = (m.sessions || []).filter(x => !x.set_at && (!m.set_at || Date.parse(x.starts_at) > Date.parse(m.set_at)))
  return held.length ? held[held.length - 1].starts_at : null
}

// The session under way at the document's moment (as_at): 1 of theirs (their group's, or every group's) that has
// started and not ended, or null. The document's next_session_at is the first session starting after its moment
// (private.accountability_log, migration 78), and a session under way joins a month's sessions only once it ends or
// they have a register mark, so between its start and the mark the open month would be "reviewed at your next session"
// on the 1 after, while Adam reviews it that evening (the review of 18 Oct's M1; Adam's yes of 22 Oct). sessions: the
// product's sessions as the page reads them (id, product_id, cohort_id, starts_at, duration_minutes, status): the
// member's page's, or Adam's beside his tab. Without them, nothing changes. Worked out from as_at, never the clock, so
// the same document with the same sessions always draws the same log. A copy kept is drawn with the sessions as they
// are now, not as they were when it was kept: if the session under way then is later cancelled or moved, the copy's
// open month names the document's own next session instead.
export function runningSession(doc, sessions) {
  if (!Array.isArray(sessions) || !sessions.length || !doc?.as_at) return null
  const at = Date.parse(doc.as_at)
  if (!Number.isFinite(at)) return null
  const group = doc.access?.cohort_id || null
  const product = doc.product?.product_id || null
  return sessions
    .filter(s => s && s.starts_at && s.status !== 'cancelled'
      && (!product || !s.product_id || s.product_id === product)
      && (!s.cohort_id || s.cohort_id === group)
      && Date.parse(s.starts_at) <= at && Date.parse(s.starts_at) + (s.duration_minutes || 0) * 60e3 > at)
    .sort((a, b) => Date.parse(a.starts_at) - Date.parse(b.starts_at))[0] || null
}

// The open month's next session: the session under way (runningSession) when it started after the month was set (so
// it's the 1 that reviews it, not the 1 it was set at), else the document's own. A start, or null.
export function nextSessionAt(m, doc, running = null) {
  if (running) {
    const from = Date.parse(m?.set_at || m?.started_at)
    if (!Number.isFinite(from) || Date.parse(running.starts_at) > from) return running.starts_at
  }
  return doc?.next_session_at || null
}
// The open month's line before its review session is held: "Reviewed at your next session on" a session still to
// come, or, when that session is the 1 under way (nextSessionAt gave running's start), "Reviewed at your session on",
// never calling a session that has started "next"
function openLineOf(c, m, doc, running) {
  const at = nextSessionAt(m, doc, running)
  if (running && at && at === running.starts_at) return LOG.openNowLine(c.ticked || 0, c.total || 0, wdm(at))
  return LOG.openLine(c.ticked || 0, c.total || 0, at ? wdm(at) : null)
}

// A session missed with its recording not watched: the line's words that link to the recording on the member's
// page (member 9, 20 Oct)
const unwatched = x => x.result === 'missed' && !x.caught_up_on

// 1 month: its heading, mark, off lines, session lines, review line and actions. missed: for each line, the session
// whose recording wasn't watched (its id), or null, so the member's page can link its words to the recording. running:
// the session under way at the document's moment (runningSession), which an open month with none held since it was set
// is reviewed at. The open month's card is titled by its month of actions ("Month 2's actions"), apart from the
// membership month in the head (the Accelerator critique's admin 2, on the member side).
export function monthView(m, doc, running = null) {
  const open = !m.closed_at
  const reviewDay = m.reviewed_at ? wdm(m.reviewed_at) : null
  const lines = []
  const missed = []
  const sessions = m.sessions || []
  if (!sessions.some(x => x.set_at)) {
    if (m.set_at) { lines.push(LOG.setAtLine(wdm(m.set_at), LOG.how.unrecorded)); missed.push(null) }
    else if (m.started_at) { lines.push(LOG.setOn(dm(m.started_at))); missed.push(null) }
  }
  for (const x of sessions) {
    lines.push(x.set_at ? LOG.setAtLine(wdm(x.starts_at), sessionHow(x)) : LOG.sessionLine(wdm(x.starts_at), sessionHow(x)))
    missed.push(unwatched(x) ? x.session_id || null : null)
  }
  const c = m.counts || {}
  const own = counted(m)
  const off = []
  if (!open && own.done < own.due) off.push(LOG.offActionsLine(own.done, own.due))
  if (own.missed) off.push(LOG.offSessionLine(own.missed, own.caught))
  const reviewAt = open ? reviewSessionOf(m) : null
  const review = open
    ? ((c.total || 0) > 0
      ? (reviewAt ? LOG.openReviewLine(c.ticked || 0, c.total || 0, wdm(reviewAt)) : openLineOf(c, m, doc, running))
      : null)
    : (m.actions || []).length
      ? LOG.reviewed({ atSession: m.reviewed_at_session, day: reviewDay, counts: { done: c.done, part: c.part_done, not: c.not_done }, carried: c.carried, next: m.next_month })
      : null
  const actions = (m.actions || []).slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)).map(a => actionView(a, m, reviewDay, Boolean(reviewAt)))
  const mark = monthMark(m)
  return {
    id: m.cycle_id, month: m.month, title: open ? LOG.monthActions(m.month) : LOG.month(m.month), mark, word: LOG.monthMark[mark] || LOG.monthMark.in_progress,
    off, lines, missed, review, actions, none: actions.length ? null : LOG.noActions, copied: Boolean(m.copied), afterTerm: Boolean(m.after_term),
    open, clean: !open && mark !== 'off_track',
  }
}

// The sessions before the first month was set (or every session, before any month), which no month lists: { title,
// lines }, or null
export function beforeView(doc) {
  const sessions = doc.before_months || []
  if (!sessions.length) return null
  const firsts = (doc.months || []).filter(m => !m.after_term).map(m => m.month)
  return {
    title: LOG.beforeH(firsts.length ? Math.min(...firsts) : null),
    lines: sessions.map(x => LOG.sessionLine(wdm(x.starts_at), sessionHow(x))),
    missed: sessions.map(x => (unwatched(x) ? x.session_id || null : null)),
  }
}

// A win's title as a sentence: its own full stop (or question mark) kept, never doubled
const asSentence = t => { const v = String(t || '').trim(); return /[.?!]$/.test(v) ? v : `${v}.` }

// 6. Wins
export function winsView(doc) {
  const wins = doc.wins || []
  return {
    count: wins.length ? LOG.winsCount(wins.length) : LOG.winsNone,
    items: wins.map(w => ({ id: w.win_id, title: asSentence(w.title), line: LOG.winLine(LOG.category[w.category] || w.category, dm(w.win_date)), detail: w.detail || null })),
  }
}

// 7. About this log: how it's kept and who to email, on the member's page and Adam's tab alike (the guarantee's rules
// are in Masterminds, apart from the log). The day it's kept from is copied.since alone: the member's version holds no
// rules since migration 81 (decision 56), and copied.since has carried the same day since 78 made both.
export function rulesView(doc, helpEmail) {
  const copiedMonths = doc.copied?.months || []
  const since = doc.copied?.since
  return { member: true, h: LOG_MEMBER.aboutH, intro: null, items: [], foot: LOG_MEMBER.foot(helpEmail), kept: copiedMonths.length && since ? LOG.keptSince(dmy(since)) : null }
}

// The whole log: everything Log.jsx draws, from 1 document. pay: the member's own page's (measures, above). sessions:
// the product's sessions beside it, for the session under way at the document's moment (runningSession), or null
export function logView(doc, { helpEmail = 'adam@adamslee.co.uk', pay = null, sessions = null } = {}) {
  if (!doc) return null
  const months = doc.product?.term_months || 12
  const running = runningSession(doc, sessions)
  const all = (doc.months || []).map(m => monthView(m, doc, running))
  const inTerm = all.filter(m => !m.afterTerm)
  const after = all.filter(m => m.afterTerm)
  const copiedMonths = (doc.copied?.months || []).slice().sort((a, b) => a - b)
  const since = doc.copied?.since
  return {
    head: {
      eyebrow: doc.product?.name || '',
      title: LOG.title,
      line: LOG.personLine({
        name: doc.person?.full_name, group: doc.access?.cohort_name, joined: doc.access?.start_day ? dmy(doc.access.start_day) : null,
        month: doc.access?.term_month, months,
      }),
      upToDate: doc.as_at ? LOG.upToDate(hm(doc.as_at), wdmy(doc.as_at)) : null,
    },
    stand: standing(doc),
    measures: measures(doc, { pay }),
    strip: strip(doc),
    stripTitle: LOG.stripH(months),
    copied: copiedMonths.length && since ? LOG.copied(copiedMonths[0], copiedMonths[copiedMonths.length - 1], dmy(since)) : null,
    months: inTerm,
    before: beforeView(doc),
    after: after.length ? { title: LOG.afterTermH(months), line: LOG.afterTermLine, months: after } : null,
    empty: all.length ? null : LOG.empty,
    wins: winsView(doc),
    rules: rulesView(doc, helpEmail),
    printFoot: LOG.printFoot(doc.person?.full_name || '', doc.as_at ? dmy(doc.as_at) : ''),
    docTitle: LOG.docTitle(doc.person?.full_name || ''),
  }
}
